import React, { useState, useEffect } from "react";
import { Modal } from "@/design-system/Atoms/Modal";
import { Button, FloatingLabelInput, Typography } from "@/design-system/Atoms";
import {
  cancelOrderFormSchema,
  type CancelOrderFormSchema,
} from "@/validation-schemas/cancel-order-schemas";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextAreaField } from "@/design-system/Atoms/TextArea";
import { useCancelOrderService } from "@/rtkQuery/features/hooks/useCancelOrderService";
import { useInViewOnce } from "@/hooks/useInViewOnce";

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  payloadOrderId?: number;
  IsSubscription: boolean;
  refetchOrders: () => void;
}

export const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  orderId = 0,
  payloadOrderId = 0,
  IsSubscription = false,
  refetchOrders,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CancelOrderFormSchema>({
    resolver: zodResolver(cancelOrderFormSchema),
    defaultValues: {
      orderId: orderId,
      reason: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    if (isOpen && orderId > 0) {
      reset({
        orderId,
        reason: "",
      });
    }
  }, [isOpen, orderId, reset]);

  const { ref } = useInViewOnce<HTMLDivElement>();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [responseFlag, setResponseFlag] = useState<boolean>(false);

  const buttonText = IsSubscription
    ? "Cancel Subscription"
    : "Request to Cancel";
  const loadingText = IsSubscription
    ? "Cancelling..."
    : "Requesting Cancellation...";
  const cardText = IsSubscription
    ? "Click the button to confirm your request to cancel your DefyED subscription. Please note that if you have an active order already with the pharmacy for fulfillment, it can no longer be canceled. This cancellation will apply to all future subscription orders."
    : "Click the button to confirm your request for cancellation. Once an order is with the pharmacy for fulfillment, it can no longer be canceled. After you submit your request, we’ll review your order details and provide an update.";

  const { submitCancelOrder } = useCancelOrderService();

  const onSubmit = async (formData: CancelOrderFormSchema) => {
    setResponseMessage(null);
    const result = await submitCancelOrder({
      orderId: payloadOrderId,
      reason: formData.reason,
    });
    const isSuccess = result?.RefundSuccessful || result?.AlreadyRefunded;
    if (isSuccess) {
      setResponseFlag(true);
    }

    if (result?.Message) {
      setResponseMessage(result.Message);
    }
    if (isSuccess) {
      reset();
      refetchOrders();
      setTimeout(() => {
        setResponseMessage(null);
        onClose();
      }, 5000);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        setResponseMessage(null);
        onClose();
      }}
      title="Cancellation Request"
      size="xl"
      className="max-h-screen max-w-[42rem]"
      childrenClassName="max-h-[calc(100vh-120px)]"
    >
      <div className="p-6">
        <div className="prose prose-sm max-w-none">
          <div className="bg-[#f6f7f9] p-6 lg:w-full mx-auto rounded-[20px]">
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="mb-6">
                <div className="w-full mb-4">
                  <FloatingLabelInput
                    id="orderId"
                    label="Order Id"
                    type="text"
                    {...register("orderId", { valueAsNumber: true })}
                    error={errors.orderId?.message}
                    readOnly
                  />
                </div>
                <div className="mb-6">
                  <TextAreaField
                    placeholder="Please provide the reason for cancellation...."
                    rows={3}
                    {...register("reason")}
                    error={errors.reason?.message}
                  />
                </div>
                <div className="mb-6">
                  <Typography
                    variant="sectionDescriptionExtraSmall"
                    as="p"
                    className="mb-4 text-center"
                  >
                    {cardText}
                  </Typography>
                </div>
              </div> 
              {responseMessage && (
                <div
                  ref={ref}
                  className={`animate-fade-in-up mt-6 mb-6 p-4 rounded-md text-sm font-bold border ${
                    responseMessage && responseFlag
                      ? "border-green-300 bg-green-50 text-green-700"
                      : "border-red-300 bg-red-50 text-red-700"
                  }`}
                  dangerouslySetInnerHTML={{ __html: responseMessage }}
                />
              )}
              <Button
                variant="secondary"
                size="xl"
                className="!w-full"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? loadingText : buttonText}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CancelOrderModal;